import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { LogoMark } from "../components/Logo";
import { ArrowRight, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 bg-[#F8F9FA]">
      <div className="w-full max-w-md bg-white border border-neutral-200 p-8 sm:p-10" data-testid="forgot-password-card">
        <div className="w-fit mb-6"><LogoMark size={48} /></div>

        {sent ? (
          <>
            <div className="w-12 h-12 bg-[#10B981]/10 text-[#10B981] flex items-center justify-center rounded-sm mb-5">
              <CheckCircle2 size={24} />
            </div>
            <h1 className="text-3xl font-black tracking-tighter mb-3" data-testid="forgot-success-title">{t.auth.forgot_sent_title}</h1>
            <p className="text-sm text-neutral-600 leading-relaxed mb-6">{t.auth.forgot_sent_body}</p>
            <Link to="/login" className="btn-primary w-full justify-center" data-testid="forgot-back-login">
              <ArrowLeft size={16} className="rtl:rotate-180" /> {t.auth.back_to_login}
            </Link>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-black tracking-tighter mb-2" data-testid="forgot-title">{t.auth.forgot_title}</h1>
            <p className="text-sm text-neutral-600 mb-7">{t.auth.forgot_subtitle}</p>

            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  type="email"
                  required
                  placeholder={t.booking.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-neutral-300 ps-10 pe-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
                  data-testid="forgot-email"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="forgot-error">{error}</div>
              )}

              <button type="submit" disabled={busy} className="w-full btn-primary justify-center disabled:opacity-60" data-testid="forgot-submit-btn">
                {busy ? t.common.loading : t.auth.forgot_submit}
                <ArrowRight size={18} />
              </button>
            </form>

            <p className="text-xs text-neutral-500 mt-6 text-center">
              <Link to="/login" className="hover:text-neutral-900 underline-offset-4 hover:underline inline-flex items-center gap-1">
                <ArrowLeft size={12} className="rtl:rotate-180" /> {t.auth.back_to_login}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
